import { Box, SimpleGrid, Td, Text } from "@chakra-ui/react";
import { useGroupBalanceStats } from "../../hooks/useGroupBalanceStats";
import StudyStatsCard from "../StudyStatsCard/StudyStatsCard";
import { StyledTable } from "../StyledTable/StyledTable";
import { GroupBalanceStats } from "../../types/GroupBalanceStats";

interface Props {
  groupId: string;
}

export const GroupBalanceView = ({ groupId }: Props) => {
  const { data: groupBalanceStats, isLoading, error } = useGroupBalanceStats(groupId);

  if (isLoading) {
    return <Text>Loading...</Text>;
  }

  if (error) {
    console.error("Error fetching group balance stats:", error);
    return <Text>Error loading data. Please try again later.</Text>;
  }

  if (!groupBalanceStats?.groupStats || !groupBalanceStats.providerStats) {
    console.log("No data available or data is incomplete");
    return <Text>No data available</Text>;
  }

  const { groupStats, providerStats } = groupBalanceStats;

  const providerRow = (row: GroupBalanceStats["providerStats"][number]) => (
    <>
      <Td>{`${row.lastName || ""} ${row.firstName || ""}`}</Td>
      <Td isNumeric>{row.totalStudies ?? "N/A"}</Td>
      <Td isNumeric>{row.totalRVUs ? Number(row.totalRVUs).toFixed(2) : "N/A"}</Td>
      <Td isNumeric>{row.pendingStudies ?? "N/A"}</Td>
      <Td isNumeric>{row.pendingRVUs ? Number(row.pendingRVUs).toFixed(2) : "N/A"}</Td>
      <Td isNumeric>
        {row.pendingAmount ? `$${Number(row.pendingAmount).toFixed(2)}` : "N/A"}
      </Td>
      <Td isNumeric>{row.paidAmount ? `$${Number(row.paidAmount).toFixed(2)}` : "N/A"}</Td>
    </>
  );

  return (
    <Box mt={16}>
      <SimpleGrid columns={{ base: 1, lg: 6 }} spacing="24px" mb="72px">
        <StudyStatsCard
          backgroundColor="#FF9760"
          title="Total Lifetime Completed Studies"
          subtitle={groupStats.totalLifetimeStudies?.toString() ?? "N/A"}
          subtitleStyle="h4"
        />
        <StudyStatsCard
          backgroundColor="#32E484"
          title="Total Lifetime RVUs Completed"
          subtitle={
            groupStats.totalLifetimeRVUs
              ? Number(groupStats.totalLifetimeRVUs).toFixed(2)
              : "N/A"
          }
          subtitleStyle="h4"
        />
        <StudyStatsCard
          backgroundColor="#91B6FE"
          title="Studies Pending Payment"
          subtitle={groupStats.pendingPaymentStudies?.toString() ?? "N/A"}
          subtitleStyle="h4"
        />
        <StudyStatsCard
          backgroundColor="#F7E376"
          title="RVU's Pending Payment"
          subtitle={
            groupStats.pendingPaymentRVUs
              ? Number(groupStats.pendingPaymentRVUs).toFixed(2)
              : "N/A"
          }
          subtitleStyle="h4"
        />
        <StudyStatsCard
          backgroundColor="#FEC0A8"
          title="Total Unmapped CPT Studies"
          subtitle={groupStats.unmappedStudies?.toString() ?? "N/A"}
          subtitleStyle="h4"
        />
        <StudyStatsCard
          backgroundColor="brandYellow.600"
          title="Pending Payment Amount"
          subtitle={
            groupStats.pendingPaymentAmount
              ? `$${Number(groupStats.pendingPaymentAmount).toFixed(2)}`
              : "N/A"
          }
          subtitleStyle="h4"
        />
      </SimpleGrid>

      <StyledTable
        columns={[
          { key: "provider", label: "Provider" },
          { key: "totalstudies", label: "Total Studies" },
          { key: "totalrvus", label: "Total RVUs" },
          { key: "pendingstudies", label: "Pending Studies" },
          { key: "pendingrvus", label: "Pending RVUs" },
          { key: "pendingamount", label: "Pending Amount" },
          { key: "paidamount", label: "Paid Amount" },
        ]}
        data={providerStats}
        customRowRenderers={providerRow}
      />
    </Box>
  );
};
